import { AccountCircle, Logout } from '@mui/icons-material'
import {
  alpha,
  darken,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material'
import { intersection } from 'lodash'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import useGetAuthSessionQuery from '~/features/auth/hooks/useGetAuthSessionQuery'
import useSignOut from '~/features/auth/hooks/useSignOut'
import { isLibbyDomain } from '~/features/partners/utils'
import useGetUser from '~/features/user/hooks/useGetUser'
import SelectAgent from '~/layouts/app/header/account/portal/selectAgent'
import { EAdminPortalClientPath, EClientPath } from '~/models/api'
import { ECognitoGroups } from '~/models/auth'
import EEnvironmentVariables from '~/models/config'
import { store } from '~/store'
import rem, { lightenOrDarken } from '~/utils/ui'

function AccountMenu() {
  const theme = useTheme()
  const { data: authSession } = useGetAuthSessionQuery()
  const [isImpersonating, userId] = store((state) => [
    !!state.user.impersonatorsUserId,
    state.user.id,
  ])

  const { data: user } = useGetUser(isImpersonating ? userId : '')

  const email = isImpersonating ? user?.data?.email : authSession?.email
  const isLibby = isLibbyDomain()
  const navigate = useNavigate()
  const { signOut } = useSignOut()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const openMenuHandler = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const closeMenuHandler = () => {
    setAnchorEl(null)
  }

  const isAdmin =
    !isImpersonating && authSession?.groups.includes(ECognitoGroups.admin)
  const isPartnerAdmin =
    !isImpersonating &&
    intersection(
      [ECognitoGroups.partnerAdmin, ECognitoGroups.partnerEditor],
      authSession?.groups,
    ).length > 0

  return (
    <>
      <IconButton
        size="large"
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={openMenuHandler}
        sx={{
          color: theme.palette.menuIcon,
          mr: { xxxs: 0, sm: 2 },
        }}
      >
        <AccountCircle fontSize="large" />
      </IconButton>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClick={closeMenuHandler}
        onClose={closeMenuHandler}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              color: theme.palette.menuFont,
              backgroundColor: theme.palette.menuBackground,
              overflow: 'visible',
              overflowY: 'auto',
              filter: `drop-shadow(${rem(0)} ${rem(2)} ${rem(8)} ${
                theme.palette.menuBackground
              })`,
              mt: 8,
              '& .MuiMenuItem-root': {
                '& .MuiListItemIcon-root > svg': {
                  color: theme.palette.menuIcon,
                  transition: 'all 0.3s ease',
                },
                '&:hover .MuiListItemIcon-root > svg': {
                  color: darken(theme.palette.menuIcon, 0.4),
                },
                paddingInline: 4,
                paddingBlock: 1,
                '& +.MuiDivider-root': {
                  marginBlock: rem(4),
                },
                '& .MuiSvgIcon-root': {
                  color: theme.palette.menuFont,
                },
                '&:not(.label):hover': {
                  backgroundColor: alpha(theme.palette.mainBackground, 0.2),
                },
                '&.label': {
                  color: lightenOrDarken(theme.palette.menuFont),
                  letterSpacing: rem(0.7),
                  fontSize: rem(14),
                  '&:hover': {
                    backgroundColor: theme.palette.menuBackground,
                    cursor: 'default',
                  },
                },
                '&.MuiButtonBase-root.Mui-disabled': {
                  opacity: 1,
                },
              },
            },
          },
        }}
      >
        <MenuItem disabled>
          <ListItemIcon>
            <AccountCircle fontSize="medium" />
          </ListItemIcon>
          {email}
        </MenuItem>

        <Divider
          sx={{ backgroundColor: alpha(theme.palette.mainBackground, 0.5) }}
          variant="fullWidth"
        />

        {import.meta.env[EEnvironmentVariables.VITE_IS_MULTIAGENT_ENABLED] ===
          'true' && <SelectAgent shouldShowOnSmallScreen />}
        <Divider
          sx={{
            backgroundColor: alpha(theme.palette.mainBackground, 0.5),
            display: { sm: 'none' },
          }}
          variant="fullWidth"
        />

        <MenuItem onClick={() => navigate(EClientPath.users)}>Users</MenuItem>

        <MenuItem
          onClick={() =>
            navigate(isLibby ? EClientPath.billing : EClientPath.account)
          }
        >
          {isLibby ? 'Billing' : 'Account'}
        </MenuItem>

        <Divider
          sx={{ backgroundColor: alpha(theme.palette.mainBackground, 0.5) }}
          variant="fullWidth"
        />

        {(isPartnerAdmin || isAdmin) && (
          <MenuItem className="label">Portals</MenuItem>
        )}
        {(isPartnerAdmin || isAdmin) && (
          <MenuItem onClick={() => navigate(EClientPath.overview)}>
            Customer Portal
          </MenuItem>
        )}
        {(isPartnerAdmin || isAdmin) && (
          <MenuItem onClick={() => navigate(EClientPath.partnerAdmin)}>
            Partner Admin Portal
          </MenuItem>
        )}
        {isAdmin && (
          <MenuItem onClick={() => navigate(EAdminPortalClientPath.admin)}>
            heyLibby Admin Portal
          </MenuItem>
        )}

        {(isPartnerAdmin || isAdmin) && (
          <Divider
            sx={{ backgroundColor: alpha(theme.palette.mainBackground, 0.5) }}
            variant="fullWidth"
          />
        )}

        <MenuItem onClick={signOut}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </>
  )
}

export default AccountMenu
